import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import App from '@/App';
import { SharedWithMeView } from '@/components/SharedWithMeView';
import { InvitePage } from '@/pages/InvitePage';
import { LoginPage } from '@/pages/LoginPage';
import PublicSubmitPage from '@/pages/PublicSubmitPage';

// Lazy load pages that are also used as modals in App.tsx
// This allows Vite to properly chunk them
const SecurityPage = lazy(() => import('@/pages/settings/SecurityPage'));
const PasskeysPage = lazy(() => import('@/pages/settings/PasskeysPage'));
const ApiKeysPage = lazy(() => import('@/pages/ApiKeysPage'));
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage'));

// Loading fallback for lazy routes
function RouteLoading() {
	return (
		<div className="flex items-center justify-center min-h-screen">
			<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
		</div>
	);
}

// =============================================================================
// Root Layout
// =============================================================================

const rootRoute = createRootRoute({
	component: () => <Outlet />,
});

// =============================================================================
// Routes
// =============================================================================

/**
 * Index route - main application
 * Renders the kanban board / link management UI
 */
const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	component: App,
});

/**
 * Login route
 * Renders the passkey-first login page
 */
const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/login',
	component: () => (
		<LoginPage
			onSuccess={() => {
				// Redirect to dashboard after successful login
				window.location.href = '/';
			}}
			redirectTo="/"
		/>
	),
});

/**
 * Invite route
 * Renders the invite landing page for new user registration
 */
const inviteRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/invite/$token',
	component: function InviteRouteComponent() {
		const { token } = inviteRoute.useParams();
		return (
			<InvitePage
				token={token}
				onSuccess={() => {
					// Redirect to dashboard after successful registration
					window.location.href = '/';
				}}
				redirectTo="/"
			/>
		);
	},
});

/**
 * Security route
 * Renders the security settings page (password management)
 */
const securityRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/settings/security',
	component: () => (
		<Suspense fallback={<RouteLoading />}>
			<SecurityPage />
		</Suspense>
	),
});

/**
 * Passkeys route
 * Renders the passkey management page
 */
const passkeysRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/settings/passkeys',
	component: () => (
		<Suspense fallback={<RouteLoading />}>
			<PasskeysPage />
		</Suspense>
	),
});

/**
 * API Keys route
 * Renders the API key management page
 */
const apiKeysRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/settings/api-keys',
	component: () => (
		<Suspense fallback={<RouteLoading />}>
			<ApiKeysPage />
		</Suspense>
	),
});

/**
 * User Management route
 * Renders the admin user management page (requires users:manage permission)
 */
const userManagementRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/admin/users',
	component: () => (
		<Suspense fallback={<RouteLoading />}>
			<UserManagementPage />
		</Suspense>
	),
});

/**
 * Shared with Me route
 * Renders links that have been shared with the current user
 */
const sharedWithMeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/shared-with-me',
	component: SharedWithMeView,
});

/**
 * Public submit route
 * Anonymous link submission to queue via public access token
 */
const publicSubmitRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/submit/$token',
	component: function PublicSubmitRouteComponent() {
		const { token } = publicSubmitRoute.useParams();
		return <PublicSubmitPage token={token} />;
	},
});

// =============================================================================
// Route Tree & Router
// =============================================================================

const routeTree = rootRoute.addChildren([
	indexRoute,
	loginRoute,
	inviteRoute,
	securityRoute,
	passkeysRoute,
	apiKeysRoute,
	userManagementRoute,
	sharedWithMeRoute,
	publicSubmitRoute,
]);

export const router = createRouter({
	routeTree,
	defaultPreload: 'intent',
});

// =============================================================================
// Type Declaration
// =============================================================================

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}
